import { TmapLog } from '@tmap-web-lib/tmap-log-client'
import { AxiosError } from 'axios'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { Header } from '@/components/Header'
import { hideJsLoadFallback, serializeError } from '@/utils'

import * as style from './error.css'

interface Props {
  error: AxiosError | undefined
  sourcePath?: string
}

const screenNameMap: Record<string, string> = {
  '/web/my': '/my/main',
  '/web': '/my/main',
  '/web/profile': '/my/profile',
  '/web/my/sns': '/my/sns_manage',
  '/web/my/sns/register': '/my/sns_register',
  '/web/my/sns/edit': '/my/sns_edit',
  '/web/all_services': '/my/all_services',
  '/web/step_counter': '/pedometer/main',
  '/web/step_counter/withdrawal': '/pedometer/withdrawal',
  '/web/move_log': '/movinglog/main',
  '/web/move_log/insight': '/movinglog/insight',
}

function getScreenName(sourcePath?: string) {
  if (!sourcePath) return '/my/main'
  return screenNameMap[sourcePath] ?? '/my/main'
}

function getLogServiceId(sourcePath?: string) {
  if (sourcePath?.includes('/step_counter')) return 'pedometer'
  if (sourcePath?.includes('/move_log')) return 'movinglog'
  return 'my'
}

function Error({ error, sourcePath }: Props) {
  const isStepCounter = sourcePath?.includes('step_counter')
  const [errorMessage, setErrorMessage] = useState<ReactNode>(null)
  const hasSentRef = useRef(false)

  useEffect(() => {
    hideJsLoadFallback()
  }, [])

  useEffect(() => {
    if (error?.response) {
      if (error.response.status === 401) {
        setErrorMessage(
          <>
            사용자 인증정보가
            <br />
            변경되었습니다.
          </>
        )
      } else {
        setErrorMessage(
          <>
            일시적으로 서비스를
            <br />
            이용할 수 없습니다.
          </>
        )
      }
    } else {
      setErrorMessage(
        <>
          일시적으로 서비스를
          <br />
          이용할 수 없습니다.
        </>
      )
    }
  }, [error])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (hasSentRef.current) return
      hasSentRef.current = true

      try {
        const displayMessage = error?.response?.status === 401
          ? '사용자 인증정보가 변경되었습니다.'
          : '일시적으로 서비스를 이용할 수 없습니다.'

        const serviceName = getLogServiceId(sourcePath)

        TmapLog('expose')
          .setServiceId('error')
          .setPageId('/error/error_exposed')
          .setCustom('display_message', displayMessage)
          .setCustom('display_type', 'full_screen')
          .setCustom('platform', 'web')
          .setCustom('service_name', serviceName)
          .setCustom('screen_name', getScreenName(sourcePath))
          .setCustom('api_name', error?.config?.url?.split('/').pop() ?? '')
          .setCustom('api_path', error?.config?.url ?? '')
          .setCustom('error_code', error?.code ?? '')
          .setCustom('error_message', error?.message ?? '')
          .setCustom('status_code', String(error?.response?.status ?? ''))
          .setCustom('error', serializeError(error))
          .setCustom('web_app_version', process.env.NEXT_PUBLIC_BUILD_VERSION as string)
          .send()
      } catch (e) {
        console.error('[Error] log send failed', e)
      }
    }, 500)

    return () => {
      clearTimeout(timer)
    }
  }, [error, sourcePath])

  const refresh = () => {
    window.location.reload()
  }

  return (
    <>
      {isStepCounter && <Header />}
      <div className={style.wrapper}>
        <p
          className={style.title}
          data-testid="error-message"
        >
          {errorMessage}
        </p>
        <p>잠시 후 다시 시도해주세요.</p>
        <button
          className={style.btn}
          onClick={refresh}
        >
          새로고침
        </button>
      </div>
    </>
  )
}

export default Error
